/**
 * @fileoverview Hook Form Layer specialized for the management of the chat note form in the Inbox.
 * Implements the **Form Layer** pattern with strict separation of responsibilities, dynamic validation and performance optimization.
 * @module useChatNoteForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 3.1.0
 */
import {
  useMemo, useCallback, useEffect, useRef
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';

/**
 * Hook Form Layer for the management and validation of the internal note form in the Inbox.
 *
 * @param {Object} params
 * @param {string|number|null} params.conversationId - Id of the selected conversation (only resets when changing)
 */
export default function useChatNoteForm({ conversationId }) {
  const { t } = useTranslation('InboxMC');
  const MAX_CHARS = 500;

  const schema = useMemo(
    () => object().shape({
      message: string()
        .required(t('chat_note.required'))
        .max(MAX_CHARS, t('chat_note.max_chars', { max: MAX_CHARS }))
    }),
    [t]
  );

  const defaultValues = useMemo(() => ({
    message: ''
  }), []);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onSubmit',
    shouldFocusError: false,
    shouldUnregister: false
  });

  const conversationIdRef = useRef(conversationId);

  const resetForm = useCallback(() => {
    methods.reset(defaultValues);
  }, [methods, defaultValues]);

  useEffect(() => {
    if (conversationIdRef.current === conversationId) return;
    conversationIdRef.current = conversationId;
    resetForm();
  }, [conversationId, resetForm]);

  return useMemo(() => ({
    methods,
    resetForm,
    maxChars: MAX_CHARS,
    placeholder: t('chat_note.placeholder')
  }), [methods, resetForm, t]);
}
